"use client";

import {
  Activity,
  Bot,
  Boxes,
  ClipboardCheck,
  DatabaseZap,
  FileClock,
  PlugZap,
  RadioTower,
  ShieldCheck,
  Truck,
} from "lucide-react";
import type { ReactNode } from "react";
import { OrchestratorConsole } from "@/components/admin/OrchestratorConsole";
import { AgentOpsDashboard } from "@/components/admin/AgentOpsDashboard";
import { AppShell } from "@/components/console/app-shell";
import { ConsoleDashboardClient } from "@/components/console/ConsoleDashboardClient";
import { Badge } from "@/components/ui/badge";
import {
  hasTenantProduct,
  type TenantMembershipSummary,
  type TenantProductScope,
} from "@/core/auth/current-tenant";
import { getConsoleSnapshot } from "@/lib/demo-data";
import { TenantStateGate } from "./TenantContextProvider";

type ProductGateProps = {
  product: TenantProductScope;
  label: string;
  children: (tenant: TenantMembershipSummary) => ReactNode;
};

export function ProductScopeGate({ product, label, children }: ProductGateProps) {
  return (
    <TenantStateGate>
      {(tenant) => {
        if (!hasTenantProduct(tenant, product)) {
          return <ProductAccessDenied label={label} tenant={tenant} />;
        }

        return children(tenant);
      }}
    </TenantStateGate>
  );
}

export function HaulbaseAgentPortal({ tenant }: { tenant: TenantMembershipSummary }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-5 py-4">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Bot size={20} />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Haulbase Agent</h1>
              <p className="text-sm text-muted-foreground">
                {tenant.tenantName} - external TMS overlay - read-and-draft agent runtime
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="info">scope: haulbase_agent</Badge>
            <Badge variant="warn">approval gated</Badge>
            <Badge variant="good">external TMS compatible</Badge>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-5 px-5 py-5">
        <section className="grid gap-3 md:grid-cols-4">
          <ScopeMetric icon={<PlugZap size={16} />} label="TMS mode" value="overlay" />
          <ScopeMetric icon={<RadioTower size={16} />} label="Signals" value="read" />
          <ScopeMetric icon={<ClipboardCheck size={16} />} label="Outputs" value="draft" />
          <ScopeMetric icon={<ShieldCheck size={16} />} label="Writes" value="approval" />
        </section>
      </main>

      <OrchestratorConsole />
      <AgentOpsDashboard />
    </div>
  );
}

export function AgenticTmsPortal({ tenant }: { tenant: TenantMembershipSummary }) {
  const snapshot = getConsoleSnapshot();

  return (
    <AppShell
      commandLoads={snapshot.loads.map(({ load }) => ({ ...load, tenantId: tenant.tenantId }))}
      title="Haulbase Agentic TMS"
      description="Full Haulbase operating system: loads, exceptions, approvals, documents, financials, analytics, and the agent runtime."
      badges={
        <>
          <Badge variant="info">scope: full_agentic_tms</Badge>
          <Badge variant="good">system of record</Badge>
          <Badge variant="warn">approval gated automation</Badge>
        </>
      }
    >
      <section className="mb-5 grid gap-3 md:grid-cols-4">
        <ScopeMetric icon={<Truck size={16} />} label="Operations" value="native" />
        <ScopeMetric icon={<Activity size={16} />} label="Agent" value="embedded" />
        <ScopeMetric icon={<FileClock size={16} />} label="Audit" value="live" />
        <ScopeMetric icon={<DatabaseZap size={16} />} label="TMS role" value="source" />
      </section>
      <ConsoleDashboardClient tenantId={tenant.tenantId} fallback={snapshot} />
    </AppShell>
  );
}

export function HeadlessTenantPortal({ tenant }: { tenant: TenantMembershipSummary }) {
  const snapshot = getConsoleSnapshot();

  return (
    <AppShell
      commandLoads={snapshot.loads.map(({ load }) => ({ ...load, tenantId: tenant.tenantId }))}
      title="Headless Haulbase"
      description="Tenant controls, API command traces, approvals, and audit visibility for headless TMS integrations."
      badges={
        <>
          <Badge variant="info">scope: headless_tms</Badge>
          <Badge variant="good">API first</Badge>
          <Badge variant="warn">idempotent commands</Badge>
        </>
      }
    >
      <ConsoleDashboardClient tenantId={tenant.tenantId} fallback={snapshot} />
    </AppShell>
  );
}

function ProductAccessDenied({
  label,
  tenant,
}: {
  label: string;
  tenant: TenantMembershipSummary;
}) {
  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-muted">
          <Boxes size={18} />
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{label} is not enabled for this tenant</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {tenant.tenantName} currently has {tenant.products.join(", ")} access.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" href="/">
            Open default workspace
          </a>
          <a className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" href="/login">
            Change account
          </a>
        </div>
      </div>
    </main>
  );
}

function ScopeMetric({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}
